import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance, { Api } from '../api/axiosInstance';
import type { Candidate } from '../../types/candidateTypes';

// ── Types ────────────────────────────────────────────────────────────────────

interface FetchCandidatesParams {
  page?: number;
  limit?: number;
  searchParams?: Record<string, string>;
}

interface PaginationParams {
  page?: number;
  limit?: number;
}

interface UploadCandidateFileResponse {
  success: boolean;
  inserted: number;
  duplicates: number;
  message: string;
  name: string;
  data?: Candidate[];
}

interface CandidateState {
  candidates: Candidate[];
  candidate: Candidate | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  limit: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  resumeUrl: string | null;
  filters: {
    jobTitles: { job_id: number; job_title: string }[];
    cities: { city: string }[];
    industries: { industry_type: string }[];
    statuses: { status: string }[];
    educationInstitutes: { institute_name: string }[];
    interviewTitles: { title: string }[];
    hrFilters: { user_id: number; first_name: string; last_name: string }[];
  };
}

// ── Initial State ────────────────────────────────────────────────────────────

const initialState: CandidateState = {
  candidates: [],
  candidate: null,
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
  limit: 10,
  pageSize: 10,
  loading: false,
  error: null,
  resumeUrl: null,
  filters: {
    jobTitles: [],
    cities: [],
    industries: [],
    statuses: [],
    educationInstitutes: [],
    interviewTitles: [],
    hrFilters: [],
  },
};

// ── Thunks: CRUD ─────────────────────────────────────────────────────────────

export const fetchCandidates = createAsyncThunk<
  {
    candidates: Candidate[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    limit: number;
  },
  (FetchCandidatesParams & { user_id?: number }) | undefined,
  { rejectValue: string }
>(
  'candidate/fetchCandidates',
  async ({ page = 1, limit = 10, searchParams = {}, user_id } = {}, { rejectWithValue }) => {
    try {
      const params: Record<string, string | number> = { page, limit };

      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) params[key] = value;
      });

      const endpoint = user_id
        ? `/candidates/by-user/${user_id}`
        : '/candidates';

      const response = await Api.get(endpoint, { params });
      const { data, meta } = response.data;

      return {
        candidates: data,
        totalCount: meta.total,
        currentPage: meta.page,
        totalPages: meta.totalPages,
        limit: meta.limit,
      };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch candidates');
    }
  }
);


export const fetchCandidateById = createAsyncThunk<
  Candidate,
  number,
  { rejectValue: string }
>('candidate/fetchCandidateById', async (candidateId, { rejectWithValue }) => {
  try {
    const response = await Api.get(`/candidates/${candidateId}`);
    // Backend returns { success, statusCode, data: { ...candidate, workHistory, educationHistory, ... }, timestamp }
    return response.data.data as Candidate;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch candidate');
  }
});
export const insertCandidate = createAsyncThunk<
  { candidateId: number; message: string },
  Record<string, any>,
  { rejectValue: string }
>('candidate/insertCandidate', async (candidateData, { rejectWithValue }) => {
  try {
    // Support both { data: {...} } wrapper and direct DTO
    const dto = candidateData.data ?? candidateData;
    const response = await Api.post('/candidates', dto);
    return { candidateId: response.data.candidateId, message: response.data.message };
  } catch (err: any) {
    let message = 'Failed to create candidate';
    if (err.response?.data) {
      if (typeof err.response.data === 'string') {
        message = err.response.data;
      } else if (err.response.data.message) {
        message = err.response.data.message;
      }
    }
    return rejectWithValue(message);
  }
});
export const createCandidate = insertCandidate;
export const updateCandidate = createAsyncThunk<
  { message: string },
  { candidateId: number; updatedData: Record<string, any> },
  { rejectValue: string }
>('candidate/updateCandidate', async ({ candidateId, updatedData }, { rejectWithValue }) => {
  try {
    const response = await Api.put(`/candidates/${candidateId}`, updatedData);
    return { message: response.data.message };
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to update candidate');
  }
});

export const deleteCandidate = createAsyncThunk<
  { candidateId: number },
  number,
  { rejectValue: string }
>('candidate/deleteCandidate', async (candidateId, { rejectWithValue }) => {
  try {
    await Api.delete(`/candidates/${candidateId}`);
    return { candidateId };
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to delete candidate');
  }
});

export const uploadCandidateFile = createAsyncThunk<
  UploadCandidateFileResponse,
  File,
  { rejectValue: string }
>('candidate/uploadCandidateFile', async (file, { rejectWithValue }) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await Api.post('/file-import/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'File upload failed');
  }
});
export const fetchJobTitleFilterByJob = createAsyncThunk<
  { job_id: number; job_title: string }[],
  PaginationParams | void,
  { rejectValue: string }
>('candidate/fetchJobTitleFilterByJob', async (params, { rejectWithValue }) => {
  try {
    const response = await Api.get('/candidates/filters/job-titles-by-job', {
      params: { page: params?.page ?? 1, limit: params?.limit ?? 50 },
    });
    return response.data.data ?? response.data ?? [];
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch job title filters');
  }
});
export const fetchAllHrFilters = createAsyncThunk<
  { user_id: number; first_name: string; last_name: string }[],
  void,
  { rejectValue: string }
>('candidate/fetchAllHrFilters', async (_, { rejectWithValue }) => {
  try {
    const response = await Api.get('/candidates/filters/hr');
    return response.data.data ?? response.data ?? [];
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch HR filters');
  }
});

export const fetchCandidateResumeUrl = createAsyncThunk<
  string,
  number,
  { rejectValue: string }
>('candidate/fetchCandidateResumeUrl', async (candidateId, { rejectWithValue }) => {
  try {
    const response = await Api.get(`/candidates/${candidateId}/resume-link`);
    return response.data.resumeUrl;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch resume URL');
  }
});

// ── Thunks: Single Resume Upload (New API) ─────────────────────────────────

export const uploadCandidateResumePdf = createAsyncThunk<
  any,
  { resume: File; job_id?: number },
  { rejectValue: string }
>('candidate/uploadCandidateResumePdf', async ({ resume, job_id }, { rejectWithValue }) => {
  try {
    const formData = new FormData();
    formData.append('resume', resume);
    if (job_id) formData.append('job_id', String(job_id));
    const response = await Api.post('/candidates/upload-resume', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Resume upload failed');
  }
});

// ── Thunks: Candidate Actions ──────────────────────────────────────────

export const moveToEmployee = createAsyncThunk<
  { employee_id: number },
  number,
  { rejectValue: string }
>('candidate/moveToEmployee', async (candidateId, { rejectWithValue }) => {
  try {
    const response = await Api.post(`/candidates/${candidateId}/move-to-employee`);
    return { employee_id: response.data.employee_id };
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to move to employee');
  }
});

export const addCandidateAdditionalInfo = createAsyncThunk<
  { message: string },
  { candidate_id: number; additional_info: string },
  { rejectValue: string }
>('candidate/addAdditionalInfo', async (dto, { rejectWithValue }) => {
  try {
    const response = await Api.post('/candidates/additional-info', dto);
    return { message: response.data.message };
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to add additional info');
  }
});

// ── Thunks: Filter Dropdowns ───────────────────────────────────────────

export const fetchCityFilter = createAsyncThunk<
  { city: string }[],
  PaginationParams | void,
  { rejectValue: string }
>('candidate/fetchCityFilter', async (params, { rejectWithValue }) => {
  try {
    const response = await Api.get('/candidates/filters/cities', {
      params: { page: params?.page ?? 1, limit: params?.limit ?? 50 },
    });
    return response.data.data ?? response.data ?? [];
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch city filter');
  }
});

export const fetchJobTitleFilter = createAsyncThunk<
  { title: string }[],
  PaginationParams | void,
  { rejectValue: string }
>('candidate/fetchJobTitleFilter', async (params, { rejectWithValue }) => {
  try {
    const response = await Api.get('/candidates/filters/job-titles', {
      params: { page: params?.page ?? 1, limit: params?.limit ?? 50 },
    });
    return response.data.data ?? response.data ?? [];
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch job title filter');
  }
});

export const fetchIndustryFilter = createAsyncThunk<
  { industry_type: string }[],
  PaginationParams | void,
  { rejectValue: string }
>('candidate/fetchIndustryFilter', async (params, { rejectWithValue }) => {
  try {
    const response = await Api.get('/candidates/filters/industries', {
      params: { page: params?.page ?? 1, limit: params?.limit ?? 50 },
    });
    return response.data.data ?? response.data ?? [];
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch industry filter');
  }
});

export const fetchStatusFilter = createAsyncThunk<
  { status: string }[],
  void,
  { rejectValue: string }
>('candidate/fetchStatusFilter', async (_, { rejectWithValue }) => {
  try {
    const response = await Api.get('/candidates/filters/statuses');
    return response.data.data ?? response.data ?? [];
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch status filter');
  }
});

export const fetchInstituteFilter = createAsyncThunk<
  { institute_name: string }[],
  PaginationParams | void,
  { rejectValue: string }
>('candidate/fetchInstituteFilter', async (params, { rejectWithValue }) => {
  try {
    const response = await Api.get('/candidates/filters/institutes', {
      params: { page: params?.page ?? 1, limit: params?.limit ?? 50 },
    });
    return response.data.data ?? response.data ?? [];
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch institute filter');
  }
});

// ── Slice ────────────────────────────────────────────────────────────────────

const candidateSlice = createSlice({
  name: 'candidate',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchCandidates
      .addCase(fetchCandidates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCandidates.fulfilled, (state, action) => {
        state.loading = false;
        state.candidates = action.payload.candidates;
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
        state.limit = action.payload.limit;
      })
      .addCase(fetchCandidates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || null;
      })

      // fetchCandidateById
      .addCase(fetchCandidateById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCandidateById.fulfilled, (state, action) => {
        state.loading = false;
        state.candidate = action.payload;
      })
      .addCase(fetchCandidateById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || null;
      })

      // insertCandidate (createCandidate)
      .addCase(insertCandidate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(insertCandidate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(insertCandidate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || null;
      })

      // updateCandidate — does NOT touch state.loading so Kanban never flickers
      .addCase(updateCandidate.pending, (_state) => {
        // intentionally left blank — optimistic updates in component
      })
      .addCase(updateCandidate.fulfilled, (state, action) => {
        // Patch the candidate's fields in-place so local state stays consistent
        const { candidateId, updatedData } = action.meta.arg;
        const idx = state.candidates.findIndex(c => c.candidate_id === candidateId);
        if (idx !== -1) {
          state.candidates[idx] = { ...state.candidates[idx], ...updatedData };
        }
      })
      .addCase(updateCandidate.rejected, (state, action) => {
        state.error = action.payload || null;
      })

      // deleteCandidate
      .addCase(deleteCandidate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCandidate.fulfilled, (state, action) => {
        state.loading = false;
        state.candidates = state.candidates.filter(c => c.candidate_id !== action.payload.candidateId);
        state.totalCount = Math.max(0, state.totalCount - 1);
      })
      .addCase(deleteCandidate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || null;
      })

      // uploadCandidateFile (CSV)
      .addCase(uploadCandidateFile.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadCandidateFile.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload;
        if (response.data && Array.isArray(response.data)) {
          state.candidates = [...response.data, ...state.candidates];
          state.totalCount += response.data.length;
        }
      })
      .addCase(uploadCandidateFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || null;
      })

      // fetchJobTitleFilterByJob
      .addCase(fetchJobTitleFilterByJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobTitleFilterByJob.fulfilled, (state, action) => {
        state.loading = false;
        state.filters.jobTitles = action.payload;
      })
      .addCase(fetchJobTitleFilterByJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch job title filters';
      })

      // fetchCandidateResumeUrl
      .addCase(fetchCandidateResumeUrl.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.resumeUrl = null;
      })
      .addCase(fetchCandidateResumeUrl.fulfilled, (state, action) => {
        state.loading = false;
        state.resumeUrl = action.payload;
      })
      .addCase(fetchCandidateResumeUrl.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || null;
        state.resumeUrl = null;
      })

      // fetchAllHrFilters
      .addCase(fetchAllHrFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllHrFilters.fulfilled, (state, action) => {
        state.loading = false;
        state.filters.hrFilters = action.payload;
      })
      .addCase(fetchAllHrFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch HR filters';
      })

      // Filter dropdowns
      .addCase(fetchCityFilter.fulfilled, (state, action) => {
        state.filters.cities = action.payload;
      })
      .addCase(fetchJobTitleFilter.fulfilled, (state, action) => {
        state.filters.interviewTitles = action.payload;
      })
      .addCase(fetchIndustryFilter.fulfilled, (state, action) => {
        state.filters.industries = action.payload;
      })
      .addCase(fetchStatusFilter.fulfilled, (state, action) => {
        state.filters.statuses = action.payload;
      })
      .addCase(fetchInstituteFilter.fulfilled, (state, action) => {
        state.filters.educationInstitutes = action.payload;
      })

      // moveToEmployee
      .addCase(moveToEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(moveToEmployee.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(moveToEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || null;
      })

      // uploadCandidateResumePdf
      .addCase(uploadCandidateResumePdf.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadCandidateResumePdf.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadCandidateResumePdf.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || null;
      });
  },
});

export default candidateSlice.reducer;